import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, CheckCircle2, CreditCard } from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

const MUTED = "#94a3b8";
const INDIGO = "#6366f1";
const BORDER = "rgba(99,102,241,0.18)";

const PLAN_DISPLAY: Record<string, string> = {
  starter: "Starter ($99 NZD/month)",
  professional: "Professional ($299 NZD/month)",
};

const TOOLS_OPTIONS = [
  { value: "spreadsheets", label: "Spreadsheets or email" },
  { value: "crm", label: "A recruitment CRM" },
  { value: "ats", label: "An ATS (like JobAdder)" },
  { value: "just_starting", label: "I'm just getting started" },
];

export default function Register() {
  const params = new URLSearchParams(window.location.search);
  const errorParam = params.get("error");
  const planParam = params.get("plan") ?? "";
  const sessionId = params.get("session_id") ?? "";

  const [formData, setFormData] = useState({ fullName: "", agencyName: "", email: "", currentTools: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(
    errorParam === "expired" ? "Your login link has expired. Please request a new one below." :
    errorParam === "blocked" ? "This account has been suspended. Please contact support@vexistalentagency.com." : ""
  );

  // Strip plan/session_id from URL bar without reload to keep URLs clean
  useEffect(() => {
    if (planParam || sessionId) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("plan");
      clean.searchParams.delete("session_id");
      const remaining = clean.search || "";
      window.history.replaceState(null, "", clean.pathname + remaining);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    if (!formData.currentTools) {
      setErrorMsg("Please select how you currently manage candidates.");
      return;
    }
    setIsLoading(true);
    try {
      const body: Record<string, string> = { ...formData };
      if (planParam) body.plan = planParam;
      if (sessionId) body.session_id = sessionId;

      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: "#05070a" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link href="/">
            <img src={vexisLogo} alt="VEXIS" className="h-12 w-auto mx-auto cursor-pointer" />
          </Link>
          <h2 className="text-2xl font-bold text-white mt-6">
            {planParam ? "Complete your registration" : "Get started with Vexis"}
          </h2>
          <p className="text-sm mt-2" style={{ color: MUTED }}>
            {planParam ? "Enter your details to activate your account." : "No credit card required. No ATS needed to get started."}
          </p>
        </div>

        {/* Paid plan banner */}
        {planParam && PLAN_DISPLAY[planParam] && (
          <div className="mb-4 rounded-xl px-4 py-3 flex items-center gap-3" style={{ background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.3)" }}>
            <CreditCard className="w-5 h-5 text-indigo-400 shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">Payment received</p>
              <p className="text-xs" style={{ color: MUTED }}>Your <span className="text-indigo-300 font-medium">{PLAN_DISPLAY[planParam]}</span> plan will activate after registration.</p>
            </div>
          </div>
        )}

        <div className="rounded-2xl p-8" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }}>
          {submitted ? (
            <div className="text-center space-y-4 py-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: "rgba(34,197,94,0.15)" }}>
                <CheckCircle2 className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">Check your email</h3>
              <p style={{ color: MUTED }} className="text-sm leading-relaxed">
                We've sent a secure login link to <span className="text-white font-medium">{formData.email}</span>. It expires in 15 minutes.
              </p>
              {planParam && (
                <p className="text-xs font-medium text-indigo-300">Your {PLAN_DISPLAY[planParam] ?? planParam} plan will activate when you log in.</p>
              )}
              <p className="text-xs mt-4" style={{ color: MUTED }}>Didn't get it? Check your spam folder or{" "}
                <button onClick={() => setSubmitted(false)} className="underline text-indigo-400 hover:text-indigo-300">try again</button>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {errorMsg && (
                <div className="rounded-lg px-4 py-3 text-sm text-amber-300" style={{ backgroundColor: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}>
                  {errorMsg}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-white text-sm">Full name</Label>
                <Input id="fullName" required placeholder="Jane Smith" value={formData.fullName}
                  onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  data-testid="input-fullname" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agencyName" className="text-white text-sm">Agency name</Label>
                <Input id="agencyName" required placeholder="Acme Recruitment" value={formData.agencyName}
                  onChange={e => setFormData({ ...formData, agencyName: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  data-testid="input-agency-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white text-sm">Work email</Label>
                <Input id="email" type="email" required placeholder="jane@acmerecruitment.com" value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500"
                  data-testid="input-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-white text-sm">How do you currently manage candidates?</Label>
                <Select value={formData.currentTools} onValueChange={val => setFormData({ ...formData, currentTools: val })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white focus:border-indigo-500" data-testid="select-current-tools">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: "#1e2235", border: `1px solid ${BORDER}` }}>
                    {TOOLS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white focus:bg-indigo-600/30">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full h-12 font-semibold text-white mt-2"
                style={{ backgroundColor: INDIGO }} disabled={isLoading} data-testid="button-register">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                {isLoading ? "Sending..." : "Send me a login link"}
              </Button>
              <p className="text-xs text-center" style={{ color: MUTED }}>
                We'll send a secure login link to your email. No password required.
              </p>
            </form>
          )}
        </div>

        <div className="mt-6 text-center space-y-3">
          <p className="text-sm" style={{ color: MUTED }}>
            Already registered?{" "}
            <Link href="/login" className="text-indigo-400 hover:text-indigo-300 underline">Sign in here</Link>
          </p>
          <div className="flex items-center justify-center gap-4 text-xs" style={{ color: MUTED }}>
            <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span>·</span>
            <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
